/**
 * A configured path must be root-relative and not protocol-relative — anything
 * else is either off-origin or resolves against the current page, so callers
 * fall back rather than emit it.
 */
const isSameOriginPath = (path: string): boolean =>
    path.startsWith("/") && !path.startsWith("//");

/**
 * Base URL for sGTM requests: the same-origin path when one is configured,
 * else the vendor host.
 */
export const getGtmBaseUrl = (fqdn: string, path: string): string => {
    if (isSameOriginPath(path)) {
        return path;
    }
    return `https://${fqdn}`;
};

/**
 * gtm.js URL up to the container id. Google's tag gateway serves the loader
 * same-origin when its path is configured; otherwise the loader comes from
 * the sGTM base URL.
 */
export const getGtmScriptUrlPrefix = (
    baseUrl: string,
    gatewayPath: string,
): string => {
    if (isSameOriginPath(gatewayPath)) {
        return `${gatewayPath}/?id=`;
    }
    return `${baseUrl}/gtm.js?id=`;
};

/**
 * JUST the GTM JS -- the `<script>` element wrapper is supplied by the caller
 * (`dangerouslySetInnerHTML`, or an imperative `script.text =`). Embedding a
 * literal `<script>...</script>` here nests a `<script>` inside a `<script>`:
 * the HTML parser closes the outer script at the inner `</script>`, so the outer
 * script's body begins with `"<script ..."` and throws "Uncaught SyntaxError:
 * Unexpected token '<'" (GTM never initializes), and the stray trailing tag
 * shifts the rest of the document, breaking hydration. The leading `<!-- ... -->`
 * lines are valid JS (Annex B HTML-like comments), so the IIFE runs as the
 * script body.
 *
 * `scriptUrlPrefix` is the gtm.js URL up to the container id.
 */
export const gtmScript = (scriptUrlPrefix: string, id: string): string => `
        <!-- Google Tag Manager -->
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        '${scriptUrlPrefix}'+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${id}');
        <!-- End Google Tag Manager -->
`;

/** Inner HTML of the no-JS `<noscript>` GTM fallback. */
export const gtmNoscriptIframe = (baseUrl: string, id: string): string =>
    `<iframe src="${baseUrl}/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
